/**
 * Watch Zone Context
 *
 * Provides watch zone state and methods throughout the app.
 * Handles CRUD operations for user watch zones and notifications.
 */

import { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { useAuth } from './AuthContext';
import {
  WatchZone,
  WatchZoneNotification,
  CreateWatchZoneInput,
  UpdateWatchZoneInput,
  fetchWatchZones,
  fetchWatchZone,
  createWatchZone,
  updateWatchZone,
  deleteWatchZone,
  toggleWatchZone,
  fetchNotifications,
  fetchUnreadNotificationCount,
  markNotificationRead,
  markAllNotificationsRead,
  dismissNotification,
} from '../api/client';

// Fallback max zones if the server doesn't report one (older server). The real
// limit is tier-aware and comes from GET /watch-zones (maxZones).
const DEFAULT_MAX_ZONES = 10;

interface WatchZoneState {
  zones: WatchZone[];
  isLoading: boolean;
  error: string | null;
  maxZones: number;
}

interface NotificationState {
  notifications: WatchZoneNotification[];
  unreadCount: number;
  isLoading: boolean;
  total: number;
}

interface WatchZoneContextType {
  // Zone state
  zones: WatchZone[];
  isLoadingZones: boolean;
  zoneError: string | null;
  maxZones: number;
  canAddZone: boolean;

  // Zone actions
  refreshZones: () => Promise<void>;
  getZone: (zoneId: string) => Promise<WatchZone | null>;
  addZone: (input: CreateWatchZoneInput) => Promise<WatchZone>;
  editZone: (zoneId: string, input: UpdateWatchZoneInput) => Promise<WatchZone>;
  removeZone: (zoneId: string) => Promise<void>;
  toggleZoneEnabled: (zoneId: string) => Promise<WatchZone>;

  // Notification state
  notifications: WatchZoneNotification[];
  unreadCount: number;
  isLoadingNotifications: boolean;
  totalNotifications: number;

  // Notification actions
  refreshNotifications: () => Promise<void>;
  loadMoreNotifications: () => Promise<void>;
  markAsRead: (notificationId: string) => Promise<void>;
  markAllAsRead: () => Promise<void>;
  dismiss: (notificationId: string) => Promise<void>;
  refreshUnreadCount: () => Promise<void>;

  // Selected zone for editing/viewing
  selectedZone: WatchZone | null;
  setSelectedZone: (zone: WatchZone | null) => void;

  // Editor state
  isEditorOpen: boolean;
  openEditor: (zone?: WatchZone) => void;
  closeEditor: () => void;
}

const WatchZoneContext = createContext<WatchZoneContextType | null>(null);

export function WatchZoneProvider({ children }: { children: ReactNode }) {
  const { isAuthenticated, getAccessToken } = useAuth();

  // Zone state
  const [zoneState, setZoneState] = useState<WatchZoneState>({
    zones: [],
    isLoading: false,
    error: null,
    maxZones: DEFAULT_MAX_ZONES,
  });

  // Notification state
  const [notificationState, setNotificationState] = useState<NotificationState>({
    notifications: [],
    unreadCount: 0,
    isLoading: false,
    total: 0,
  });

  // UI state
  const [selectedZone, setSelectedZone] = useState<WatchZone | null>(null);
  const [isEditorOpen, setIsEditorOpen] = useState(false);

  // Load zones when authenticated
  useEffect(() => {
    if (isAuthenticated) {
      refreshZones();
      refreshUnreadCount();
    } else {
      // Clear state when logged out
      setZoneState({ zones: [], isLoading: false, error: null, maxZones: DEFAULT_MAX_ZONES });
      setNotificationState({ notifications: [], unreadCount: 0, isLoading: false, total: 0 });
      setSelectedZone(null);
      setIsEditorOpen(false);
    }
  }, [isAuthenticated]);

  // Zone actions
  const refreshZones = useCallback(async () => {
    if (!isAuthenticated) return;

    setZoneState(prev => ({ ...prev, isLoading: true, error: null }));
    try {
      const token = await getAccessToken();
      if (!token) throw new Error('Not authenticated');

      const { zones, maxZones } = await fetchWatchZones(token);
      setZoneState({ zones, isLoading: false, error: null, maxZones });
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to load watch zones';
      setZoneState(prev => ({ ...prev, isLoading: false, error: message }));
    }
  }, [isAuthenticated, getAccessToken]);

  const getZone = useCallback(async (zoneId: string): Promise<WatchZone | null> => {
    const token = await getAccessToken();
    if (!token) return null;

    try {
      return await fetchWatchZone(zoneId, token);
    } catch {
      return null;
    }
  }, [getAccessToken]);

  const addZone = useCallback(async (input: CreateWatchZoneInput): Promise<WatchZone> => {
    const token = await getAccessToken();
    if (!token) throw new Error('Not authenticated');

    if (zoneState.zones.length >= zoneState.maxZones) {
      throw new Error(
        zoneState.maxZones <= 1
          ? 'Free accounts can have 1 watch zone. Become a supporter to watch more areas.'
          : `Maximum of ${zoneState.maxZones} watch zones allowed`
      );
    }

    const zone = await createWatchZone(input, token);
    setZoneState(prev => ({ ...prev, zones: [...prev.zones, zone] }));
    return zone;
  }, [getAccessToken, zoneState.zones.length, zoneState.maxZones]);

  const editZone = useCallback(async (zoneId: string, input: UpdateWatchZoneInput): Promise<WatchZone> => {
    const token = await getAccessToken();
    if (!token) throw new Error('Not authenticated');

    const updated = await updateWatchZone(zoneId, input, token);
    setZoneState(prev => ({
      ...prev,
      zones: prev.zones.map(z => z.zoneId === zoneId ? updated : z),
    }));

    // Update selected zone if it was the one edited
    if (selectedZone?.zoneId === zoneId) {
      setSelectedZone(updated);
    }

    return updated;
  }, [getAccessToken, selectedZone]);

  const removeZone = useCallback(async (zoneId: string): Promise<void> => {
    const token = await getAccessToken();
    if (!token) throw new Error('Not authenticated');

    await deleteWatchZone(zoneId, token);
    setZoneState(prev => ({
      ...prev,
      zones: prev.zones.filter(z => z.zoneId !== zoneId),
    }));

    // Clear selected zone if it was deleted
    if (selectedZone?.zoneId === zoneId) {
      setSelectedZone(null);
    }
  }, [getAccessToken, selectedZone]);

  const toggleZoneEnabled = useCallback(async (zoneId: string): Promise<WatchZone> => {
    const token = await getAccessToken();
    if (!token) throw new Error('Not authenticated');

    const updated = await toggleWatchZone(zoneId, token);
    setZoneState(prev => ({
      ...prev,
      zones: prev.zones.map(z => z.zoneId === zoneId ? updated : z),
    }));

    return updated;
  }, [getAccessToken]);

  // Notification actions
  const refreshNotifications = useCallback(async () => {
    if (!isAuthenticated) return;

    setNotificationState(prev => ({ ...prev, isLoading: true }));
    try {
      const token = await getAccessToken();
      if (!token) throw new Error('Not authenticated');

      const response = await fetchNotifications(token, { limit: 20 });
      setNotificationState({
        notifications: response.notifications,
        unreadCount: response.notifications.filter(n => n.status === 'UNREAD').length,
        isLoading: false,
        total: response.total,
      });
    } catch (error) {
      console.error('Failed to load notifications:', error);
      setNotificationState(prev => ({ ...prev, isLoading: false }));
    }
  }, [isAuthenticated, getAccessToken]);

  const loadMoreNotifications = useCallback(async () => {
    if (!isAuthenticated || notificationState.isLoading) return;

    setNotificationState(prev => ({ ...prev, isLoading: true }));
    try {
      const token = await getAccessToken();
      if (!token) throw new Error('Not authenticated');

      const response = await fetchNotifications(token, {
        skip: notificationState.notifications.length,
        limit: 20,
      });

      setNotificationState(prev => ({
        ...prev,
        notifications: [...prev.notifications, ...response.notifications],
        isLoading: false,
        total: response.total,
      }));
    } catch (error) {
      console.error('Failed to load more notifications:', error);
      setNotificationState(prev => ({ ...prev, isLoading: false }));
    }
  }, [isAuthenticated, getAccessToken, notificationState.notifications.length, notificationState.isLoading]);

  const refreshUnreadCount = useCallback(async () => {
    if (!isAuthenticated) return;

    try {
      const token = await getAccessToken();
      if (!token) return;

      const count = await fetchUnreadNotificationCount(token);
      setNotificationState(prev => ({ ...prev, unreadCount: count }));
    } catch (error) {
      console.error('Failed to fetch unread count:', error);
    }
  }, [isAuthenticated, getAccessToken]);

  const markAsRead = useCallback(async (notificationId: string) => {
    const token = await getAccessToken();
    if (!token) return;

    try {
      await markNotificationRead(notificationId, token);
      setNotificationState(prev => ({
        ...prev,
        notifications: prev.notifications.map(n =>
          n.notificationId === notificationId ? { ...n, status: 'READ' as const } : n
        ),
        unreadCount: Math.max(0, prev.unreadCount - 1),
      }));
    } catch (error) {
      console.error('Failed to mark notification as read:', error);
    }
  }, [getAccessToken]);

  const markAllAsRead = useCallback(async () => {
    const token = await getAccessToken();
    if (!token) return;

    try {
      await markAllNotificationsRead(token);
      setNotificationState(prev => ({
        ...prev,
        notifications: prev.notifications.map(n => ({ ...n, status: 'READ' as const })),
        unreadCount: 0,
      }));
    } catch (error) {
      console.error('Failed to mark all as read:', error);
    }
  }, [getAccessToken]);

  const dismiss = useCallback(async (notificationId: string) => {
    const token = await getAccessToken();
    if (!token) return;

    try {
      const notification = notificationState.notifications.find(n => n.notificationId === notificationId);
      await dismissNotification(notificationId, token);
      setNotificationState(prev => ({
        ...prev,
        notifications: prev.notifications.filter(n => n.notificationId !== notificationId),
        unreadCount: notification?.status === 'UNREAD' ? Math.max(0, prev.unreadCount - 1) : prev.unreadCount,
        total: prev.total - 1,
      }));
    } catch (error) {
      console.error('Failed to dismiss notification:', error);
    }
  }, [getAccessToken, notificationState.notifications]);

  // Editor controls
  const openEditor = useCallback((zone?: WatchZone) => {
    setSelectedZone(zone || null);
    setIsEditorOpen(true);
  }, []);

  const closeEditor = useCallback(() => {
    setIsEditorOpen(false);
    // Don't clear selectedZone immediately to allow for animation
    setTimeout(() => setSelectedZone(null), 300);
  }, []);

  const value: WatchZoneContextType = {
    // Zone state
    zones: zoneState.zones,
    isLoadingZones: zoneState.isLoading,
    zoneError: zoneState.error,
    maxZones: zoneState.maxZones,
    canAddZone: zoneState.zones.length < zoneState.maxZones,

    // Zone actions
    refreshZones,
    getZone,
    addZone,
    editZone,
    removeZone,
    toggleZoneEnabled,

    // Notification state
    notifications: notificationState.notifications,
    unreadCount: notificationState.unreadCount,
    isLoadingNotifications: notificationState.isLoading,
    totalNotifications: notificationState.total,

    // Notification actions
    refreshNotifications,
    loadMoreNotifications,
    markAsRead,
    markAllAsRead,
    dismiss,
    refreshUnreadCount,

    // Selected zone
    selectedZone,
    setSelectedZone,

    // Editor state
    isEditorOpen,
    openEditor,
    closeEditor,
  };

  return (
    <WatchZoneContext.Provider value={value}>
      {children}
    </WatchZoneContext.Provider>
  );
}

export function useWatchZones() {
  const context = useContext(WatchZoneContext);
  if (!context) {
    throw new Error('useWatchZones must be used within a WatchZoneProvider');
  }
  return context;
}
